import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css';
import HeroImage from './HeroImage';
import { useAuth } from '../contexts/AuthContext';
import starLogo from '../assets/star-logo.svg';

// Icons from react-icons (if available) - otherwise we'll use text emojis
const icons = {
  moon: '🌙',
  stars: '✨',
  book: '📚',
  magic: '✨',
  cloud: '☁️',
  sparkles: '✨',
  robot: '🤖',
  child: '👶',
  heart: '❤️',
  sleep: '💤',
  brain: '🧠',
  family: '👪',
  check: '✓',
  arrow: '→',
  wand: '🪄',
  rainbow: '🌈',
  castle: '🏰',
  dragon: '🐉',
  fairy: '🧚‍♀️',
};

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const headerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  // Calculate and apply padding based on header height
  useEffect(() => {
    const adjustPadding = () => {
      if (headerRef.current && heroRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        // Add an extra safety margin of 20px
        heroRef.current.style.paddingTop = `${headerHeight + 30}px`;
      }
    };

    // Run on mount and on window resize
    adjustPadding();
    window.addEventListener('resize', adjustPadding);

    // Recheck after a short delay to make sure everything has rendered
    const timeoutId = setTimeout(adjustPadding, 500);

    return () => {
      window.removeEventListener('resize', adjustPadding);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="landing-page">
      <header className="landing-header" ref={headerRef}>
        <div className="landing-logo">
          <img src={starLogo} alt="Wisporia Logo" />
          <h1>Wisporia</h1>
        </div>
        
        {/* Navigation buttons for all screen sizes */}
        <nav className="landing-nav">
          {user ? (
            <Link to="/app" className="cta-button">Go to App</Link>
          ) : (
            <div className="auth-nav-links">
              <Link to="/login" className="auth-button login-button">Login</Link>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section with improved imagination theme */}
      <section className="hero" ref={heroRef}>
        <div className="hero-content">
          <h1>
            <span className="imagination-highlight">Unlock</span> Your Child's 
            <span className="imagination-word"> Imagination</span>
          </h1>
          <p className="hero-description">
            Create personalized AI-powered bedtime stories that transport your little ones to 
            magical worlds filled with wonder, adventure, and dreams. Watch as their eyes light up 
            with endless possibilities night after night.
          </p>
          <div className="cta-buttons">
            <Link to="/app" className="cta-button large">
              Create Your Story <span className="cta-icon">{icons.wand}</span>
            </Link>
            <a href="#how-it-works" className="secondary-cta">
              See How It Works <span className="arrow">{icons.arrow}</span>
            </a>
          </div>
          
          {/* Imagination indicators */}
          <div className="imagination-indicators">
            <div className="imagination-tag">
              <span className="tag-icon">{icons.dragon}</span> Adventure
            </div>
            <div className="imagination-tag">
              <span className="tag-icon">{icons.castle}</span> Fantasy
            </div>
            <div className="imagination-tag">
              <span className="tag-icon">{icons.rainbow}</span> Wonder
            </div>
            <div className="imagination-tag">
              <span className="tag-icon">{icons.fairy}</span> Magic
            </div>
          </div>
        </div>
        
        <div className="hero-image">
          {/* Replace with our new static HeroImage component */}
          <HeroImage 
            className="hero-generated-image"
          />
          
          <div className="image-caption">
            Every story opens a new universe of possibilities
          </div>
        </div>
      </section>

      {/* Features Section with enhanced imagination theme */}
      <section id="features" className="features">
        <h2>Spark <span className="highlight">Wonder & Creativity</span> at Bedtime</h2>
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">{icons.magic}</div>
            <h3>Endless Story Worlds</h3>
            <p>AI-crafted tales tailored exactly to your child's interests, creating new magical worlds every night.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">{icons.sparkles}</div>
            <h3>Enchanting Narration</h3>
            <p>Lifelike voices bring characters and adventures to life with emotion, warmth and clarity.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">{icons.robot}</div>
            <h3>Personalized Magic</h3>
            <p>Include your child's name, favorite activities, and beloved characters in every adventure.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">{icons.sleep}</div>
            <h3>Dreamy Soundscapes</h3>
            <p>Gentle background sounds create the perfect atmosphere to drift into peaceful sleep.</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works">
        <h2>Creating <span className="highlight">Bedtime Magic</span> is Easy</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Customize Your Story</h3>
            <p>Select characters, setting, theme, and add your child's name to personalize the adventure.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Watch AI Weave Magic</h3>
            <p>Our AI crafts a unique, engaging story based on your selections in seconds.</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Enjoy the Journey</h3>
            <p>Listen together as professional narration and soothing sounds create an immersive experience.</p>
          </div>
        </div>
        <Link to="/app" className="cta-button">
          Begin Your Adventure <span className="arrow">{icons.arrow}</span>
        </Link>
      </section>

      {/* NEW - Illustrated Books Section */}
      <section className="illustrated-books">
        <div className="section-badge">
          <span className="new-feature">NEW FEATURE</span>
        </div>
        <div className="section-title">
          <h2>Turn Stories Into Magical Keepsakes</h2>
        </div>
        <div className="books-showcase">
          <div className="books-image">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/images/book-story.jpeg`}
              alt="Illustrated storybook with child character"
              className="book-display-image"
            />
          </div>
          <div className="books-content">
            <h3>Bring Stories to Life with Illustrated Books</h3>
            <p>Transform your personalized AI bedtime stories into beautiful hardcover books featuring magical illustrations of your child in fantastical settings. Give the gift of imagination that lasts forever.</p>
            <ul className="books-features">
              <li><span className="check-icon">✓</span> AI-generated images of your child in the story</li>
              <li><span className="check-icon">✓</span> Premium hardcover book delivered to your door</li>
              <li><span className="check-icon">✓</span> Create cherished keepsakes your family will treasure</li>
            </ul>
            <div className="books-cta">
              <Link to="/create-story" className="cta-button book-cta">Create a Storybook</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <h2>What <span className="highlight">Families</span> Are Saying</h2>
        <div className="testimonial-carousel">
          <div className="testimonial">
            <p className="quote">
              "My daughter insists on a new AI story every night now! She loves being the main character in her own adventures. These stories have sparked conversations about bravery and kindness we wouldn't have had otherwise."
            </p>
            <p className="author">- Sarah T., mom of 2</p>
          </div>
          <div className="testimonial">
            <p className="quote">
              "As a busy parent who's not naturally creative with stories, this app has been a game-changer. The quality of writing and narration is incredible, and my son is developing a real love for storytelling."
            </p>
            <p className="author">- Michael K., dad of 1</p>
          </div>
          <div className="testimonial">
            <p className="quote">
              "The background sounds are magical - they help my twins settle down and create a perfect bedtime atmosphere. The stories are the perfect length for our bedtime routine!"
            </p>
            <p className="author">- Emily R., mom of twins</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="benefits-content">
          <h2>The Magic of <span className="highlight">Bedtime Stories</span></h2>
          <ul className="benefits-list">
            <li>
              <div className="benefit-icon">{icons.brain}</div>
              <div className="benefit-text">
                <h3>Growing Brilliant Minds</h3>
                <p>Stories develop vocabulary, listening skills, and imagination in ways screen time simply can't match.</p>
              </div>
            </li>
            <li>
              <div className="benefit-icon">{icons.heart}</div>
              <div className="benefit-text">
                <h3>Stronger Connections</h3>
                <p>Create special moments that strengthen your parent-child bond through shared adventures.</p>
              </div>
            </li>
            <li>
              <div className="benefit-icon">{icons.sleep}</div>
              <div className="benefit-text">
                <h3>Peaceful Sleep Routine</h3>
                <p>Establish a calming bedtime ritual that signals it's time to rest and prepare for dreams.</p>
              </div>
            </li>
            <li>
              <div className="benefit-icon">{icons.child}</div>
              <div className="benefit-text">
                <h3>Building Confidence</h3>
                <p>Stories featuring your child as the hero build self-esteem, courage, and emotional intelligence.</p>
              </div>
            </li>
          </ul>
        </div>
        <div className="benefits-image">
          {/* Also replace this GeneratedImage with a regular img since we're keeping the DALL-E image approach */}
          <img 
            src={`${process.env.PUBLIC_URL}/assets/images/bedtime-story-parent.jpg`}
            alt="Parent reading a bedtime story to child"
            className="benefits-static-image"
          />
        </div>
      </section>

      {/* Final CTA Section */}
      <div className="final-cta">
        <div className="cta-decoration">
          <div className="stars-big">{icons.stars}</div>
          <div className="stars-small">{icons.stars}</div>
          <div className="moon-big">{icons.moon}</div>
          <div className="fairy-element">{icons.fairy}</div>
          <div className="castle-element">{icons.castle}</div>
        </div>
        <div className="cta-content">
          <h2>Begin Your <span className="highlight-light">Magical Journey</span> Tonight</h2>
          <p>
            Create endless personalized adventures that'll make bedtime the most anticipated part of your child's day.
            Every story opens a new door to imagination and wonder!
          </p>
          {user ? (
            <Link to="/app" className="cta-button large glow">
              Create Your First Story <span className="arrow">{icons.wand}</span>
            </Link>
          ) : (
            <Link to="/signup" className="cta-button large glow">
              Get Started <span className="arrow">{icons.wand}</span>
            </Link>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-logo">Wisporia</div>
          <div className="footer-social">
            &copy; {new Date().getFullYear()} Wisporia. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 